import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { UserRoleSet } from '@paragon/contract-library';
import { hasSealsAndSignaturesPermission, isLoggedInUserRoot, UserRoleEditor } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';

import { startFreeTrial, updateUser } from '../../actions/userActions';
import { getOrganizationsSorted } from '../../selectors/organizations';
import { UserFromSelector, userToEdit } from '../../selectors/users';
import { RootState } from '../../store';
import { UserProperties } from '../personal/UserProperties';
import { getAreAnyErrors, validate } from './validate';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = StateProps & DispatchProps;

interface State {
  readonly showErrors: boolean;
  readonly userRecord: UserFromSelector;
}

class EditUser extends React.Component<Props, State> {
  state: State = {
    showErrors: false,
    userRecord: this.props.user,
  };

  onUserChange = (record: UserFromSelector) => {
    this.setState((prevState) => {
      const updatedUserRecord = { ...prevState.userRecord, ...record };
      return { userRecord: updatedUserRecord };
    });
  };

  onUserSetChange = (value: UserRoleSet) => {
    this.setState((prevState) => ({ userRecord: { ...prevState.userRecord, roleSet: value } }));
  };

  onSave = () => {
    if (this.validate()) {
      const updatedUser = { ...this.props.user, ...this.state.userRecord };
      this.props.updateUser(updatedUser, updatedUser.email);
      this.setState({ showErrors: false });
    }
  };

  onUpdateTrialEndDate = async (trialEndDate: Date) => {
    const updatedUser = await this.props.updateUser(
      { ...this.props.user, trialEndDate: trialEndDate.toISOString() },
      this.props.user.email,
    );
    if (updatedUser) {
      this.setState((prevState) => ({
        userRecord: { ...prevState.userRecord, trialEndDate: updatedUser.trialEndDate },
      }));
    }
  };

  onStartFreeTrial = async () => {
    const updatedUser = await this.props.startFreeTrial(this.props.user.email);
    if (updatedUser) {
      this.setState((prevState) => ({
        userRecord: { ...prevState.userRecord, roleSet: updatedUser.roleSet, trialEndDate: updatedUser.trialEndDate },
      }));
    }
  };

  validate() {
    const updatedUser = { ...this.props.user, ...this.state.userRecord };
    const areAnyErrors = getAreAnyErrors(updatedUser);

    if (areAnyErrors) {
      this.setState({ showErrors: true });
      this.props.enqueueError('Please fill all required fields');
      return false;
    }

    return true;
  }

  render() {
    const trialEndDate = this.state.userRecord.trialEndDate ? new Date(this.state.userRecord.trialEndDate) : null;
    const hasOngoingTrial = trialEndDate && trialEndDate > new Date(new Date().toDateString());
    const isFreeUserOrHasOngoingTrial = this.state.userRecord.roleSet.role === 'Free' || hasOngoingTrial;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '25%', padding: 1 }}>
        <Typography variant="h6">Edit Selected User</Typography>
        <Divider sx={{ mb: 4 }} />
        <Paper sx={{ p: 2, pt: 0, bgcolor: 'backContrast.main' }} elevation={1}>
          <UserProperties
            user={this.state.userRecord}
            organizations={this.props.organizations}
            onChange={this.onUserChange}
            validate={validate}
            showErrors={this.state.showErrors}
          />

          {this.props.isLoggedInUserRoot && isFreeUserOrHasOngoingTrial ? (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {hasOngoingTrial ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Trial End Date"
                    formatDensity="dense"
                    minDate={dayjs()}
                    value={dayjs(trialEndDate)}
                    onChange={(date) => (date ? this.onUpdateTrialEndDate(date.toDate()) : null)}
                    sx={{ mt: 2, width: '100%' }}
                  />
                </LocalizationProvider>
              ) : (
                <Button variant="outlined" sx={{ mt: 2, width: '100%' }} onClick={this.onStartFreeTrial}>
                  Start Trial
                </Button>
              )}
            </Box>
          ) : null}

          {this.props.isLoggedInUserRoot ? (
            <UserRoleEditor
              value={this.state.userRecord.roleSet}
              onChange={this.onUserSetChange}
              showOtherPermissions={this.props.isLoggedInUserRoot}
            />
          ) : null}
        </Paper>
        <Button
          variant="contained"
          sx={{
            mt: 3,
            mb: 1,
            py: 1,
            width: 150,
            color: 'buttonColor.main',
            bgcolor: 'backContrast.main',
            '&:hover': {
              bgcolor: 'primary.dark',
              color: '#fff',
            },
          }}
          onClick={this.onSave}
        >
          Save
        </Button>
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: userToEdit(state)!,
  organizations: getOrganizationsSorted(state),
  isLoggedInUserRoot: isLoggedInUserRoot(state),
  hasSealsAndSignaturesPermission: hasSealsAndSignaturesPermission(state),
});

const actionCreators = {
  enqueueError,
  updateUser,
  startFreeTrial,
};

export default connect(mapStateToProps, actionCreators)(EditUser);
